import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthContext";

import AdminLayout from "../layouts/AdminLayout";
import HeaderStats from "../components/Headers/HeaderStats";
import CardBarChart from "../components/Cards/CardBarChart";
import CardTopClients from "../components/Cards/CardTopClients";
import CardTopProducts from "../components/Cards/CardTopProducts";

import {
  getPurchasesSummary,
  getPurchasesOverTime,
  getTopSuppliers,
  getTopPurchasedProducts,
} from "../controllers/apiReports";

import { getClients } from "../controllers/api_clients";

// Etiquetas del HeaderStats para el panel de Compras.
const LABELS_COMPRAS = {
  neto: "TOTAL NETO COMPRAS",
  facturado: "TOTAL COMPRAS",
  nc: "NOTAS DE CRÉDITO RECIBIDAS",
  nd: "NOTAS DE DÉBITO RECIBIDAS",
};

export default function DashboardCompras({ collapsed }) {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.rol === "admin" || user?.rol === "auditor";
  const empresa_id = user?.empresa_id;
  const nombreUsuario = user?.nombre || "Usuario";

  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(isAdmin ? "" : empresa_id);
  const [topSuppliersLimit, setTopSuppliersLimit] = useState(5);
  const [topProductsLimit, setTopProductsLimit] = useState(5);
  const [currencyView, setCurrencyView] = useState("VES");

  const applyFilter = () => { setAppliedStart(draftStart); setAppliedEnd(draftEnd); };
  const clearFilter = () => {
    setDraftStart(""); setDraftEnd(""); setAppliedStart(""); setAppliedEnd("");
    if (isAdmin) setSelectedClientId("");
  };

  const clientsQuery = useQuery({
    queryKey: ["clients"],
    enabled: isAdmin,
    initialData: [],
    queryFn: async () => {
      const res = await getClients();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const effectiveClientId = isAdmin ? (selectedClientId || undefined) : empresa_id;
  const params = {
    ...(effectiveClientId && { empresa_id: effectiveClientId }),
    ...(appliedStart && { start_date: appliedStart }),
    ...(appliedEnd && { end_date: appliedEnd }),
    currency: currencyView,
  };

  const summaryQuery = useQuery({
    queryKey: ["purchases-summary", params],
    enabled: isAdmin || !!empresa_id,
    queryFn: () => getPurchasesSummary(params),
    keepPreviousData: true,
  });

  const overTimeQuery = useQuery({
    queryKey: ["purchases-over-time", params],
    enabled: isAdmin || !!empresa_id,
    keepPreviousData: true,
    initialData: { currency: "VES", data: [] },
    queryFn: async () => {
      const res = await getPurchasesOverTime(params);
      return { currency: res?.currency || "VES", data: Array.isArray(res?.data) ? res.data : [] };
    },
  });

  const topSuppliersQuery = useQuery({
    queryKey: ["top-suppliers", params],
    enabled: isAdmin || !!empresa_id,
    initialData: [],
    queryFn: async () => {
      const res = await getTopSuppliers(params);
      return Array.isArray(res) ? res : [];
    },
  });

  const topProductsQuery = useQuery({
    queryKey: ["top-purchased-products", params],
    enabled: isAdmin || !!empresa_id,
    initialData: [],
    queryFn: async () => {
      const res = await getTopPurchasedProducts(params);
      return Array.isArray(res) ? res : [];
    },
  });

  return (
    <AdminLayout nombreUsuario={nombreUsuario}>
      {/* HERO */}
      <div className="relative w-full rounded-3xl overflow-hidden shadow-sirius-lg">
        <div className="absolute inset-0 bg-sirius-night" />
        <div className="absolute inset-0 bg-sirius-mesh opacity-30" />
        <div className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-spark-500/25 blur-[110px]" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-sirius-600/25 blur-[110px]" />

        <div className="relative px-5 md:px-8 py-6">
          <div className="mb-5">
            <h1 className="text-white font-display font-bold text-2xl md:text-3xl leading-tight">
              Panel de <span className="text-gradient-sirius">Compras</span>
            </h1>
            <p className="text-white/55 text-sm mt-1">
              Resumen de tu actividad de compras (montos en Bs.)
            </p>
          </div>

          {/* FILTROS */}
          <div className="flex flex-wrap gap-2 items-center pb-4">
            <span className="text-xs text-white/50 font-semibold uppercase tracking-wider mr-1">Filtrar por:</span>
            {isAdmin && (
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-spark-400/40 [&>option]:bg-[#1A1A1A] [&>option]:text-white"
              >
                <option value="">Todas las empresas</option>
                {clientsQuery.data.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre_empresa}</option>
                ))}
              </select>
            )}
            <input type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-spark-400/40" />
            <input type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-spark-400/40" />
            <select
              value={currencyView}
              onChange={(e) => setCurrencyView(e.target.value)}
              className="rounded-lg px-3 py-1.5 text-sm bg-white/10 border border-white/20 text-white backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-spark-400/40 [&>option]:bg-[#1A1A1A] [&>option]:text-white"
              title="Moneda de visualización"
            >
              <option value="VES">VES (Bs.)</option>
              {(!isAdmin || effectiveClientId) && (
                <option value="extranjera">Moneda extranjera</option>
              )}
              {isAdmin && (
                <>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </>
              )}
            </select>
            <button onClick={applyFilter}
              className="inline-flex items-center gap-1.5 bg-white text-sirius-700 hover:bg-sirius-50 px-4 py-1.5 rounded-lg text-sm font-display font-semibold transition-colors shadow-spark">
              <i className="fas fa-filter text-xs" /> Aplicar
            </button>
            <button onClick={clearFilter}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Limpiar filtros">
              <i className="fa-solid fa-eraser" />
            </button>
          </div>

          {/* STATS */}
          <HeaderStats
            summary={summaryQuery.data}
            isLoading={summaryQuery.isFetching}
            collapsed={collapsed}
            labels={LABELS_COMPRAS}
          />

          <div className="mt-3 flex items-start gap-2 text-xs text-white/60">
            <i className="fas fa-circle-info mt-0.5 text-white/50" />
            <p className="leading-relaxed">
              Los totales están en bolívares (Bs.), base fiscal del Libro de Compras.
              El "TOTAL COMPRAS" suma facturas e importaciones; las notas de crédito recibidas restan.
            </p>
          </div>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="px-4 md:px-8 w-full overflow-hidden">
        <div className="w-full pt-6 overflow-hidden">
          <CardBarChart
            data={overTimeQuery.data.data}
            currency={overTimeQuery.data.currency}
            isLoading={overTimeQuery.isFetching}
            title="Compras por Periodo"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-6">
          <CardTopClients
            clients={topSuppliersQuery.data.slice(0, topSuppliersLimit)}
            limit={topSuppliersLimit}
            onChangeLimit={setTopSuppliersLimit}
            currency={overTimeQuery.data.currency}
            title="Top Proveedores"
          />
          <CardTopProducts
            products={topProductsQuery.data.slice(0, topProductsLimit)}
            limit={topProductsLimit}
            onChangeLimit={setTopProductsLimit}
            currency={overTimeQuery.data.currency}
            title="Top Productos Comprados"
          />
        </div>
      </div>
    </AdminLayout>
  );
}
